import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';

import { Badge, Card, EmptyState, ErrorNotice, PageHeader, Spinner, Stat, StatusDot } from '@/components/ui';
import { useI18n } from '@/i18n';
import { api } from '@/lib/api';
import { ago, dateLong, dateShort, time } from '@/lib/format';
import { useAlertText } from '@/lib/useAlertText';
import { useLabel } from '@/lib/useLabel';

/**
 * Flags worth showing next to a status badge.
 *
 * `auto_closed` is both a status and a flag, so rendering both produces the same
 * chip twice. The status badge already says it.
 */
function displayFlags(status: string, flags: string[]): string[] {
  return flags.filter((f) => !(f === 'auto_closed' && status === 'auto_closed'));
}

export function DashboardPage() {
  const { t } = useI18n();
  const L = useLabel();
  const alertText = useAlertText();
  const { data, isLoading, error } = useQuery({
    queryKey: ['dashboard'],
    queryFn: api.dashboard,
    // Someone is watching this screen to see who is on site right now.
    refetchInterval: 60_000,
  });

  // Same source as the Scadenze page and the sidebar badge, so all three agree.
  const alerts = useQuery({ queryKey: ['alerts', false], queryFn: () => api.alerts(false) });
  const topAlerts = (alerts.data?.alerts ?? []).slice(0, 5);

  if (isLoading) return <Spinner />;
  if (error) return <ErrorNotice error={error} />;
  if (!data) return null;

  const scheduledToday = data.todayJobs.reduce((sum, r) => sum + r.count, 0);

  return (
    <>
      <PageHeader title={t('dashboard.title')} subtitle={dateLong(new Date())} />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <Stat label={t('dashboard.onDutyNow')} value={data.clockedIn.length} tone="success" />
        <Stat label={t('dashboard.jobsToday')} value={scheduledToday} tone="primary" />
        <Stat
          label={t('dashboard.toReview')}
          value={data.exceptions.length}
          tone={data.exceptions.length > 0 ? 'warning' : 'neutral'}
          hint={t('dashboard.toReviewHint')}
        />
        <Stat
          label={t('dashboard.unassigned')}
          value={data.unassignedJobs.length}
          tone={data.unassignedJobs.length > 0 ? 'danger' : 'neutral'}
          hint={t('dashboard.unassignedHint')}
        />
      </div>

      {/*
        Two independent columns, not a 2x2 grid. In a grid each row is as tall as its
        taller card, so a short "On duty now" sat stretched beside a long review list
        with a hole underneath. Short and long are paired so the columns end together.
      */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-start">
        <div className="space-y-6 min-w-0">
          {/* Who's on site right now */}
          <section>
            <h2 className="font-bold mb-3">{t('dashboard.onDutyNow')}</h2>
            <Card padded={false}>
              {data.clockedIn.length === 0 ? (
                <div className="px-4 py-8 text-sm text-muted text-center">
                  {t('dashboard.nobodyOnDuty')}
                </div>
              ) : (
                <div className="table-scroll">
                  <table>
                    <tbody>
                      {data.clockedIn.map((row) => (
                        <tr key={row.id}>
                          <td className="td">
                            <Link to={`/shifts/${row.id}`} className="font-semibold hover:text-primary">
                              {row.user_name}
                            </Link>
                            <div className="text-xs text-muted truncate">
                              {row.reference ? `${row.reference} · ` : ''}
                              {row.title ?? t('common.noJob')}
                            </div>
                          </td>
                          <td className="td text-end whitespace-nowrap">
                            <div className="tabular font-semibold">{time(row.clock_in_at)}</div>
                            <div className="text-xs text-muted">{ago(row.clock_in_at)}</div>
                          </td>
                          <td className="td w-px">
                            {row.clock_in_within_geofence === true ? (
                              <StatusDot tone="success">{t('shifts.onSite')}</StatusDot>
                            ) : row.clock_in_within_geofence === false ? (
                              <StatusDot tone="warning">{t('common.outsideArea')}</StatusDot>
                            ) : (
                              <StatusDot tone="neutral">{t('shifts.gpsUnavailable')}</StatusDot>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Card>
          </section>

          <section>
            {/* Driven by the alert queue, not by `user_certification`. Reading a different
                source here is how the dashboard came to say "nessuna certificazione in
                scadenza" while Scadenze listed twelve — and the dashboard is the screen
                people believe. */}
            <div className="flex items-center gap-2 mb-3">
              <h2 className="font-bold">{t('dashboard.expiriesToHandle')}</h2>
              <Link to="/alerts" className="text-sm font-semibold text-primary hover:underline ms-auto">
                {t('dashboard.seeAll')}
              </Link>
            </div>
            <Card padded={false}>
              {topAlerts.length === 0 ? (
                <div className="px-4 py-8 text-sm text-muted text-center">
                  {alerts.isLoading
                    ? t('common.loading')
                    : t('dashboard.noExpiries')}
                </div>
              ) : (
                <div className="table-scroll">
                  <table>
                    <tbody>
                      {topAlerts.map((a) => (
                        <tr key={a.id}>
                          <td className="td">
                            {a.userId ? (
                              <Link
                                to={`/employees/${a.userId}`}
                                className="font-semibold hover:text-primary"
                              >
                                {alertText(a).title}
                              </Link>
                            ) : (
                              <span className="font-semibold">{alertText(a).title}</span>
                            )}
                            <div className="text-xs text-muted">{alertText(a).body}</div>
                          </td>
                          <td className="td text-end whitespace-nowrap">
                            <Badge tone={L('severity', a.severity).tone}>
                              {a.dueOn ? dateShort(a.dueOn) : L('severity', a.severity).label}
                            </Badge>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Card>
          </section>

        </div>
        <div className="space-y-6 min-w-0">
          {/* The queue that actually needs a human. Ordered first for that reason. */}
          <section>
            <h2 className="font-bold mb-3">{t('dashboard.shiftsToReview')}</h2>
            <Card padded={false}>
              {data.exceptions.length === 0 ? (
                <div className="px-4 py-8 text-sm text-muted text-center">
                  {t('dashboard.noShiftsToReview')}
                </div>
              ) : (
                <div className="table-scroll">
                  <table>
                    <tbody>
                      {data.exceptions.slice(0, 8).map((row) => {
                        const status = L('shift', row.status);
                        return (
                          <tr key={row.id}>
                            <td className="td">
                              <Link to={`/shifts/${row.id}`} className="font-semibold hover:text-primary">
                                {row.user_name}
                              </Link>
                              <div className="text-xs text-muted tabular">
                                {dateShort(row.clock_in_at)} · {time(row.clock_in_at)}
                                {row.clock_out_at ? `–${time(row.clock_out_at)}` : ''}
                              </div>
                            </td>
                            <td className="td">
                              <div className="flex flex-wrap gap-1 justify-end">
                                {row.status !== 'closed' ? (
                                  <Badge tone={status.tone}>{status.label}</Badge>
                                ) : null}
                                {displayFlags(row.status, row.exception_flags).map((f) => {
                                  const spec = L('flag', f);
                                  return (
                                    <Badge key={f} tone={spec.tone}>
                                      {spec.label}
                                    </Badge>
                                  );
                                })}
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </Card>
          </section>

          <section>
            <h2 className="font-bold mb-3">{t('dashboard.jobsWithoutEngineer')}</h2>
            <Card padded={false}>
              {data.unassignedJobs.length === 0 ? (
                <div className="px-4 py-8 text-sm text-muted text-center">
                  {t('dashboard.allJobsAssigned')}
                </div>
              ) : (
                <div className="table-scroll">
                  <table>
                    <tbody>
                      {data.unassignedJobs.map((job) => (
                        <tr key={job.id}>
                          <td className="td">
                            <Link to={`/jobs/${job.id}`} className="font-semibold hover:text-primary">
                              {job.title}
                            </Link>
                            <div className="text-xs text-muted tabular">{job.reference}</div>
                          </td>
                          <td className="td text-end text-sm text-muted tabular whitespace-nowrap">
                            {job.scheduled_start ? dateShort(job.scheduled_start) : '—'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Card>
          </section>
        </div>
      </div>

      {data.clockedIn.length === 0 &&
      data.exceptions.length === 0 &&
      data.unassignedJobs.length === 0 ? (
        <EmptyState title={t('dashboard.allClear')} message={t('dashboard.allClearHint')} />
      ) : null}
    </>
  );
}
